<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useShopfloorApi } from '../composables/useShopfloorApi'
import type { ShiftDashboardData } from '../types/shopfloor'

const { t } = useI18n()
const { fetchDashboard } = useShopfloorApi()

const dashboard = ref<ShiftDashboardData | null>(null)
const loading = ref(true)
const error = ref(false)

onMounted(async () => {
  try {
    dashboard.value = await fetchDashboard()
  } catch {
    error.value = true
  } finally {
    loading.value = false
  }
})

function retry() {
  error.value = false
  loading.value = true
  fetchDashboard()
    .then((data) => { dashboard.value = data })
    .catch(() => { error.value = true })
    .finally(() => { loading.value = false })
}
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('shopfloor.dashboard.eyebrow') }}</p>
      <h2>{{ t('shopfloor.dashboard.title') }}</h2>
      <p>{{ t('shopfloor.dashboard.description') }}</p>
    </div>
  </div>

  <!-- Loading -->
  <div v-if="loading" class="state-message">
    <p>{{ t('shopfloor.dashboard.loading') }}</p>
  </div>

  <!-- Error -->
  <div v-else-if="error" class="state-message error">
    <p>{{ t('shopfloor.dashboard.error') }}</p>
    <button class="btn-primary" @click="retry">{{ t('shopfloor.dashboard.retry') }}</button>
  </div>

  <!-- Dashboard -->
  <template v-else-if="dashboard">
    <div class="kpi-grid">
      <div class="kpi-card">
        <span class="kpi-value">{{ dashboard.activeOrders }}</span>
        <span class="kpi-label">{{ t('shopfloor.dashboard.activeOrders') }}</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-value">{{ dashboard.totalPlannedPairs.toLocaleString() }}</span>
        <span class="kpi-label">{{ t('shopfloor.dashboard.planned') }}</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-value">{{ dashboard.totalProducedPairs.toLocaleString() }}</span>
        <span class="kpi-label">{{ t('shopfloor.dashboard.produced') }}</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-value">{{ dashboard.completionRate }}%</span>
        <span class="kpi-label">{{ t('shopfloor.dashboard.completion') }}</span>
      </div>
    </div>

    <div class="panel-grid">
      <!-- Bottlenecks -->
      <section class="panel panel-wide">
        <h3>{{ t('shopfloor.dashboard.bottlenecks') }}</h3>
        <div v-if="dashboard.bottlenecks.length === 0" class="empty-inline">
          {{ t('shopfloor.dashboard.noBottlenecks') }}
        </div>
        <ul v-else class="bottleneck-list">
          <li v-for="(b, index) in dashboard.bottlenecks" :key="`${b.orderCode}-${b.stage}-${index}`" class="bottleneck-item">
            <RouterLink
              class="bottleneck-link"
              :to="{ path: `/orders/${b.orderId}`, query: { stage: b.stageCode } }"
            >
              <span class="bottleneck-stage">{{ b.stage }}</span>
              <span class="bottleneck-detail">{{ b.orderCode }} · {{ b.completion }}%</span>
            </RouterLink>
          </li>
        </ul>
        <p class="bottleneck-hint">{{ t('shopfloor.dashboard.bottleneckHint') }}</p>
      </section>

      <!-- Exception Summary -->
      <section class="panel">
        <h3>{{ t('shopfloor.dashboard.rejectsRework') }}</h3>
        <p class="panel-stat">{{ dashboard.rejectedPairs.toLocaleString() }}</p>
        <p class="panel-sub">{{ t('shopfloor.dashboard.rejectedPairs', { count: dashboard.rejectedPairs, rework: dashboard.reworkPairs }) }}</p>
      </section>

      <!-- Employee assignment -->
      <section class="panel">
        <h3>{{ t('shopfloor.dashboard.employeeAssignment') }}</h3>
        <p class="panel-stat">{{ dashboard.assignedEmployees }}</p>
        <p class="panel-sub">{{ t('shopfloor.dashboard.employeesAssigned', { count: dashboard.assignedEmployees }) }}</p>
      </section>

      <!-- Machine summary -->
      <section class="panel">
        <h3>{{ t('shopfloor.dashboard.machineSummary') }}</h3>
        <p class="panel-stat">{{ dashboard.runningMachines }}</p>
        <p class="panel-sub">{{ t('shopfloor.dashboard.running', { count: dashboard.runningMachines }) }} · {{ t('shopfloor.dashboard.idle', { count: dashboard.idleMachines }) }}</p>
      </section>
    </div>
  </template>
</template>

<style scoped>
.page-heading {
  margin-bottom: 24px;
}
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }

.state-message {
  padding: 40px 0;
  text-align: center;
  color: #5f6b6a;
}
.state-message.error { color: #b33a3a; }

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}
.kpi-card {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  padding: 20px;
  text-align: center;
}
.kpi-value {
  display: block;
  font-size: 32px;
  font-weight: 800;
  color: #1a2b26;
}
.kpi-label {
  display: block;
  font-size: 12px;
  color: #65736e;
  text-transform: uppercase;
  margin-top: 4px;
  font-weight: 600;
}

.panel-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.panel-wide {
  grid-column: 1 / -1;
}
.panel {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  padding: 20px;
}
.panel h3 {
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 700;
  color: #1a2b26;
}
.panel-stat {
  font-size: 28px;
  font-weight: 800;
  color: #1a2b26;
  margin: 0;
}
.panel-sub {
  margin: 2px 0 0;
  font-size: 13px;
  color: #65736e;
}
.empty-inline {
  color: #8b9a95;
  font-size: 13px;
  font-style: italic;
}
.bottleneck-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.bottleneck-item {
  padding: 0;
  border-bottom: 1px solid #f0f2f1;
}
.bottleneck-item:last-child { border-bottom: none; }
.bottleneck-link {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  text-decoration: none;
}
.bottleneck-link:hover .bottleneck-stage { text-decoration: underline; }
.bottleneck-stage { font-weight: 600; color: #b3472d; }
.bottleneck-detail { color: #65736e; font-size: 13px; }
.bottleneck-hint { margin: 10px 0 0; font-size: 12px; color: #8b9a95; }

.btn-primary {
  display: inline-block;
  margin-top: 12px;
  padding: 8px 20px;
  background: #27705d;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.btn-primary:hover { background: #1f5c4b; }

@media (max-width: 900px) {
  .kpi-grid { grid-template-columns: repeat(2, 1fr); }
  .panel-grid { grid-template-columns: 1fr; }
}
</style>
